import { useParams, Link, useNavigate } from 'react-router-dom';
import { WidePageLayout } from '../components/WidePageLayout';
import useEpics from '../hooks/useEpics';
import RichTextContent from '../components/RichTextContent';
import type { Epic } from '../types/epic';
import PlusIcon from '../components/icons/PlusIcon';
import Edit2Icon from '../components/icons/Edit2Icon';
import Trash2Icon from '../components/icons/Trash2Icon';

export function BoardEpics() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { epics, deleteEpic } = useEpics(id || '0');

  const handleDeleteEpic = async (epicId: number, epicName: string) => {
    if (confirm(`Are you sure you want to delete epic "${epicName}"?`)) {
      try {
        await deleteEpic(epicId);
      } catch {
        // Error is handled by the hook
      }
    }
  };

  return (
    <div className="space-y-6">
      <WidePageLayout>
        <div className="flex items-center justify-between">
          <div>
            <Link
              to={`/boards/${id}`}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              ← Back to Board
            </Link>
            <h1 className="text-3xl font-bold text-white mt-2">Epics</h1>
            <p className="text-gray-400 mt-1">
              Manage and view epics for this board ({epics.length} epics)
            </p>
          </div>
          <Link
            to={`/boards/${id}/epics/new`}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Create Epic
          </Link>
        </div>
      </WidePageLayout>

      <WidePageLayout>
        {epics.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-xl font-bold text-white mb-2">No Epics Yet</h2>
              <p className="text-gray-400 mb-6">
                Create your first epic to start organizing your work.
              </p>
              <Link
                to={`/boards/${id}/epics/new`}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Create First Epic
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {epics.map((epic) => (
              <EpicCard
                key={epic.id}
                epic={epic}
                onClick={() => navigate(`/boards/${id}/epics/${epic.id}`)}
                onDelete={() => handleDeleteEpic(epic.id, epic.name)}
                onEdit={() => navigate(`/boards/${id}/epics/${epic.id}/edit`)}
              />
            ))}
          </div>
        )}
      </WidePageLayout>
    </div>
  );
}

interface EpicCardProps {
  epic: Epic;
  onClick: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

function EpicCard({ epic, onClick, onDelete, onEdit }: EpicCardProps) {
  const defaultColor = '#3b82f6';
  const color = epic.color || defaultColor;

  return (
    <div
      className="bg-gray-800 rounded-lg border border-gray-700 hover:border-gray-600 transition-all cursor-pointer overflow-hidden group"
      onClick={onClick}
    >
      {/* Color header */}
      <div className="h-2" style={{ backgroundColor: color }} />

      <div className="p-4">
        {/* Header with actions */}
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-semibold text-white flex-1">{epic.name}</h3>
          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="text-gray-400 hover:text-white transition-colors p-1"
              title="Edit epic"
            >
              <Edit2Icon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="text-gray-400 hover:text-red-400 transition-colors p-1"
              title="Delete epic"
            >
              <Trash2Icon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Description */}
        {epic.description && (
          <p className="text-gray-400 text-sm mb-3 line-clamp-2">{epic.description}</p>
        )}

        {/* Rich text content preview */}
        {epic.content && Array.isArray(epic.content) && epic.content.length > 0 && (
          <div className="mb-3 max-h-[120px] overflow-hidden relative">
            <RichTextContent
              content={epic.content}
              className="text-sm text-gray-400 line-clamp-3"
            />
            {/* Fade overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-800 to-transparent pointer-events-none" />
          </div>
        )}

        {/* Footer with metrics */}
        <div className="flex items-center justify-between text-xs text-gray-500 mt-3 pt-3 border-t border-gray-700">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <span>📊</span>
              {epic.metrics?.card_count || 0} cards
            </span>
            {epic.parent_epic_id && (
              <span className="flex items-center gap-1">
                <span>🔄</span>
                Sub-epic
              </span>
            )}
          </div>
          {epic.date_last_activity && (
            <span>{new Date(epic.date_last_activity).toLocaleDateString()}</span>
          )}
        </div>

        {/* Closed badge */}
        {epic.closed && (
          <div className="mt-3">
            <span className="inline-block px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded">
              Closed
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default BoardEpics;
